import { useMemo } from 'preact/hooks';
import type { OccupiedWindow } from '../components/ui/datetime/useDateTimePicker.js';
import { useFetch } from './useFetch.js';

/**
 * 205 (#292): a car's already-committed windows — a paid or confirmed booking's hold, or a maintenance/staff block —
 * read once per car id from the public endpoint (no auth needed).
 *
 * 386 (#1301, FR-015): shared by the booking form and the car page. The car page used to have no windows at all, so a
 * customer could choose any window there, carry it into the booking form, and be refused only at payment. `settled`
 * (the read is over, ok or not) lets the booking form wait before pre-filling a carried window (FR-016). A read that
 * did not succeed yields no windows, so callers keep their old behaviour without a separate flag.
 */
export interface OccupiedWindowsRead {
  windows: OccupiedWindow[];
  settled: boolean;
}

export function useOccupiedWindows(
  carId: string | null | undefined,
  opts?: { rental?: string },
): OccupiedWindowsRead {
  // 386 slice 2 (#1278): the server widens other rentals by the car's gap; an extension passes its own rental, which
  // the server then leaves out (its own legs and their gap must never block it).
  const rental = opts?.rental ? `?rental=${encodeURIComponent(opts.rental)}` : '';
  const read = useFetch<{ items: Array<{ start_at: string; end_at: string }> }>(
    carId ? `/cars/${carId}/blocks/public${rental}` : null,
  );
  const items = read.status === 'ok' ? read.data.items : null;
  // Stable identity while the read is unchanged, so the pickers' memoised slot lists do not recompute every render.
  const windows = useMemo(
    () => (items ?? []).map((b) => ({ start: new Date(b.start_at), end: new Date(b.end_at) })),
    [items],
  );
  return {
    windows,
    settled: read.status !== 'loading',
  };
}
